import { cn } from '@/lib/utils';
import {
	getProntuarioPastSessionVisibleFields,
	type ProntuarioPastSessionRecord,
} from '../../types/prontuarioPastSession';
import {
	PRONTUARIO_SESSION_DETAIL_LABEL_CLASS,
	PRONTUARIO_SESSIONS_SUBTITLE_CLASS,
	PRONTUARIO_SESSIONS_TITLE_CLASS,
} from '../../constants/prontuarioTheme';
import { resolveProntuarioPastSessionSummary } from '../../utils/resolveProntuarioPastSessionSummary';
import { PbtsTagChips } from '@/components/pbts/PbtsTagChips';
import { ProntuarioPastSessionField } from './ProntuarioPastSessionField';
import { ProntuarioPastSessionTasksList } from './ProntuarioPastSessionTasksList';

type SessionCallProntuarioPastSessionDetailViewProps = {
	record: ProntuarioPastSessionRecord;
	isLoading?: boolean;
	isError?: boolean;
	postProcessStatus?: MedicalRecords.MedicalRecordSessionPosProcessStatus;
	className?: string;
};

const DETAIL_EXCLUDED_FIELDS = new Set(['data', 'sessaoNumero', 'resumo']);

const SessionCallProntuarioPastSessionDetailView = ({
	record,
	isLoading = false,
	isError = false,
	postProcessStatus,
	className,
}: SessionCallProntuarioPastSessionDetailViewProps) => {
	const visibleFields = getProntuarioPastSessionVisibleFields(record).filter(
		({ key }) => !DETAIL_EXCLUDED_FIELDS.has(key),
	);
	const summaryText = resolveProntuarioPastSessionSummary(record);
	const sessionTasks = record.tasks ?? [];
	const hasTasks = sessionTasks.length > 0;
	const isProcessing =
		postProcessStatus === 'QUEUED' || postProcessStatus === 'STARTED';
	const isFailed = postProcessStatus === 'FAILED';

	return (
		<div className={cn('min-w-0 pb-4', className)}>
			<div className="flex items-start justify-between gap-3">
				<h2 className={PRONTUARIO_SESSIONS_TITLE_CLASS}>
					Sessão {record.sessaoNumero ?? '—'}
				</h2>
				{record.data?.trim() ? (
					<p
						className={cn(
							PRONTUARIO_SESSIONS_SUBTITLE_CLASS,
							'shrink-0 pt-0.5 text-right',
						)}
					>
						{record.data.trim()}
					</p>
				) : null}
			</div>

			{isLoading ? (
				<p className={cn(PRONTUARIO_SESSIONS_SUBTITLE_CLASS, 'mt-6')}>
					Carregando sessão...
				</p>
			) : null}

			{!isLoading && isError ? (
				<p className={cn(PRONTUARIO_SESSIONS_SUBTITLE_CLASS, 'mt-6')}>
					Não foi possível carregar esta sessão.
				</p>
			) : null}

			{!isLoading && !isError && isProcessing ? (
				<p className={cn(PRONTUARIO_SESSIONS_SUBTITLE_CLASS, 'mt-6')}>
					Gerando resumo da sessão...
				</p>
			) : null}

			{!isLoading && !isError && isFailed && !summaryText ? (
				<p className={cn(PRONTUARIO_SESSIONS_SUBTITLE_CLASS, 'mt-6')}>
					Não foi possível gerar o resumo desta sessão.
				</p>
			) : null}

			{summaryText ? (
				<div className="mt-4 rounded-xl border border-white/[0.08] bg-[#1a2430] px-3.5 py-3">
					<p className={PRONTUARIO_SESSION_DETAIL_LABEL_CLASS}>
						Resumo
					</p>
					<p className="mt-2 text-[14px] leading-[1.6] text-[#bdc1c6]">
						{summaryText}
					</p>
					<PbtsTagChips
						value={record.resumoTag ?? record.resumoCurtoTag}
						className="mt-3"
					/>
				</div>
			) : null}

			{visibleFields.length === 0 ? (
				!summaryText &&
				!hasTasks &&
				!isLoading &&
				!isError &&
				!isProcessing &&
				!isFailed ? (
					<p
						className={cn(
							PRONTUARIO_SESSIONS_SUBTITLE_CLASS,
							'mt-6',
						)}
					>
						Nenhuma anotação registrada para esta sessão.
					</p>
				) : null
			) : (
				<div className="mt-5 grid grid-cols-1 gap-3.5 sm:grid-cols-2">
					{visibleFields.map((field) => (
						<ProntuarioPastSessionField
							key={`${record.id}-${field.key}`}
							record={record}
							field={field}
						/>
					))}
				</div>
			)}

			<ProntuarioPastSessionTasksList tasks={sessionTasks} />
		</div>
	);
};

export { SessionCallProntuarioPastSessionDetailView };
